import { useState, useEffect } from "react";

// ─── Types ─────────────────────────────────────────────────────────────────
interface ExchangeRates {
  baseCurrency: "DKK";
  rates: Record<string, number>; // e.g. { EUR: 7.46, USD: 6.82 }
  lastUpdated: string;           // ISO date string
  isLoading: boolean;
  error: string | null;
}

interface CachedRates {
  rates: Record<string, number>;
  fetchedAt: string; // ISO date (YYYY-MM-DD)
}

// ─── Constants ─────────────────────────────────────────────────────────────
const STORAGE_KEY = "afima_exchange_rates";
const SUPPORTED_CURRENCIES = ["EUR", "USD"];

// ─── Helper: check if cache is from today ──────────────────────────────────
function isCacheFresh(cache: CachedRates): boolean {
  const today = new Date().toISOString().split("T")[0];
  return cache.fetchedAt === today;
}

// ─── Fetch rates from open.er-api.com (free, CORS-friendly, daily updates) ──
async function fetchRates(): Promise<Record<string, number>> {
  const results: Record<string, number> = {};

  // EUR → DKK
  const eurRes = await fetch("https://open.er-api.com/v6/latest/EUR");
  if (!eurRes.ok) throw new Error(`EUR rate fetch failed: ${eurRes.status}`);
  const eurData = await eurRes.json();
  results.EUR = eurData.rates.DKK;

  // USD → DKK
  const usdRes = await fetch("https://open.er-api.com/v6/latest/USD");
  if (!usdRes.ok) throw new Error(`USD rate fetch failed: ${usdRes.status}`);
  const usdData = await usdRes.json();
  results.USD = usdData.rates.DKK;

  return results;
}

// ─── Hook ──────────────────────────────────────────────────────────────────
export function useCurrencyRates(): ExchangeRates {
  const [rates, setRates] = useState<Record<string, number>>({});
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadRates() {
      // 1. Try localStorage cache first
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const cached: CachedRates = JSON.parse(raw);
          if (isCacheFresh(cached) && Object.keys(cached.rates).length > 0) {
            if (!cancelled) {
              setRates(cached.rates);
              setLastUpdated(cached.fetchedAt);
              setIsLoading(false);
            }
            return;
          }
        }
      } catch {
        // Cache corrupted, continue to fetch
      }

      // 2. Fetch fresh rates
      try {
        const freshRates = await fetchRates();
        const today = new Date().toISOString().split("T")[0];

        // Save to localStorage
        const cache: CachedRates = { rates: freshRates, fetchedAt: today };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));

        if (!cancelled) {
          setRates(freshRates);
          setLastUpdated(today);
          setIsLoading(false);
        }
      } catch (err) {
        console.error("Failed to fetch exchange rates:", err);

        // Fall back to hardcoded approximate rates
        const fallback: Record<string, number> = { EUR: 7.46, USD: 6.82 };
        if (!cancelled) {
          setRates(fallback);
          setLastUpdated("fallback");
          setError(err instanceof Error ? err.message : "Failed to fetch rates");
          setIsLoading(false);
        }
      }
    }

    loadRates();
    return () => { cancelled = true; };
  }, []);

  return { baseCurrency: "DKK", rates, lastUpdated, isLoading, error };
}

// ─── Utility functions (can be used outside React) ─────────────────────────

/**
 * Convert an amount from a given currency to DKK.
 * Returns null if the rate is not available.
 */
export function toDKK(
  amount: number,
  fromCurrency: string,
  rates: Record<string, number>
): number | null {
  if (fromCurrency === "DKK") return amount;
  const rate = rates[fromCurrency];
  if (!rate) return null;
  return amount * rate;
}

/**
 * Format a DKK amount for display.
 */
export function formatDKK(amount: number): string {
  return `${amount.toLocaleString("da-DK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} kr`;
}

/**
 * Get cached rates synchronously (for non-React contexts).
 * Returns null if no cache exists.
 */
export function getCachedRates(): CachedRates | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export { SUPPORTED_CURRENCIES };
